import React, {
	useRef,
	forwardRef,
	useMemo,
	useState,
	useEffect
} from "react";
import { Icons, Box, Button, IconButton, InputAdornment, TextField } from "@curves/ui-base";
import clsx from "clsx";
import { isEmpty, isEqual, isFunction } from "@util/utils";
import { BATCH_DEDUCTION } from "@icoach/batchPayment/staticData";
import { OverviewListTable, useLabelCheckBox } from "@icoach/components";
import usePrivacyToggle, { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import useSearch from "@util/hook/useSearch";
import { getCheckRowsLabel } from "@icoach/batchPayment/CheckRowsLabel";

export const BatchControllerBoxContainer = ({ children, className }) => {
	return (
		<Box className={clsx("batch-controller-box", className)}>
			<Box className={clsx("ml-auto btn-group tool-btn-group mb-2")}>
				{children}
			</Box>
		</Box>
	);
};

/** =========================================================
 * 📌 批次功能中控
 *  =========================================================
 */
const BatchActionTable = forwardRef((props, ref) => {
	const {
		list,
		dialogRef,
		headerRow,
		batchActions,
		refresh,
		tableRowHeaderProps,
		tableRowBodyProps
	} = props || {};
	const {
		checkBoxsRef,
		headerCheckBoxRef,
		onHeaderChange,
		onCellChange,
		getCheckedItems,
		getCheckedSourceData,
		doClearCheckedItems
	} = useLabelCheckBox();
	const { isRevealed, doShowPrivacy, doHidePrivacy } = usePrivacyToggle(false);
	const [activeAction, setActiveAction] = useState("");
	const { component: ActionComponent } = batchActions[activeAction] || {};

	const {
		updateSearchKeyword,
		filteredData,
		inputRef,
		handleChange,
		handleComposition
	} = useSearch(list, ["memberName", "memberNo"]);


	const actionRef = useRef();

	// 更新批次操作統計資訊
	const updateSelectionStats = () => {
		let list = getCheckedSourceData();
		let params = {
			count: list.length
		};

		if (isEqual(activeAction, BATCH_DEDUCTION)) {
			params.totalAmount = list.reduce((acc, item) => {
				const itemPrice = item?.totalPrice || item?.transactionPrice; // 在 billing 清單中, 沒有 totalPrice 會有 transactionPrice
				return acc + itemPrice;
			}, 0);
		}

		actionRef.current && actionRef.current.setSelectionStats && actionRef.current.setSelectionStats(params);
	};

	// 選擇全部
	const handleHeaderChange = (isCheckedState) => {
		onHeaderChange(isCheckedState);
		updateSelectionStats();
	};

	// 選擇單筆
	const handleCellChange = (isCheckedState) => {
		onCellChange(isCheckedState);
		updateSelectionStats();
	};

	// 開啟隱碼
	const handleOpenReveal = () => {
		const apiParams = {
			filterType: securityMaskFilterTypeMap.ClubOrderBatchPaymentOverview,
			totalCount: filteredData.length
		};
		doShowPrivacy(apiParams);
	};

	// 停止批次操作, 關閉隱碼, 清除勾選, 關閉批次操作介面
	const handleHideAction = () => {
		doHidePrivacy();
		doClearCheckedItems();
		setActiveAction("");
	};

	// 點擊批次操作按鈕
	const handleClickButton = (value) => {
		if (activeAction) {
			handleHideAction();
		} else {
			setActiveAction(value);
			handleOpenReveal();
		}
	};

	// 批次操作按鈕列表
	const Buttons = useMemo(() => {
			return Object.keys(batchActions).map((key) => {
				const { label, value, startIcon, onClick } = batchActions[key];
				return (
					<Button key={value} color={"grey"} startIcon={isFunction(startIcon) ? startIcon(isRevealed) : startIcon}
							onClick={() => {
								handleClickButton(value);
								isFunction(onClick) && onClick(value);
							}}>
						{label}
					</Button>
				);
			});
		},
		// eslint-disable-next-line
		[batchActions]);


	// 批次操作表格標題
	const handleRowForAction = useMemo(() => {
			return [getCheckRowsLabel(), ...headerRow];
		},
		// eslint-disable-next-line
		[headerRow]);


	// 排序扣款列表
	const sortedPaymentList = useMemo(() => {
			if (isEmpty(filteredData)) return [];

			// 先排序「是否可以扣款」再排序「訂單狀態」
			return [...filteredData].sort((a, b) => {
				if (b.canPayment > a.canPayment) return 1;
				if (b.canPayment < a.canPayment) return -1;

				if (b.status > a.status) return 1;
				if (b.status < a.status) return -1;

				return 0;
			});
		},
		// eslint-disable-next-line
		[filteredData]);

	useEffect(() => {
			if (!!activeAction && !isRevealed) {
				handleOpenReveal();
			}
		},
		// eslint-disable-next-line
		[activeAction]);

	return (
		<>
			<BatchControllerBoxContainer>
				{Buttons}
				<TextField
					style={{ maxWidth: "260px" }}
					ref={inputRef}
					placeholder={"搜尋會員"}
					InputProps={{
						endAdornment: (
							<InputAdornment position={"end"}>
								<IconButton onClick={() => {updateSearchKeyword()}}>
									<Icons.Magnifier />
								</IconButton>
							</InputAdornment>
						)
					}}
					onCompositionStart={handleComposition}
					onCompositionUpdate={handleComposition}
					onCompositionEnd={handleComposition}
					onChange={handleChange}
				/>
			</BatchControllerBoxContainer>
			{React.isValidElement(ActionComponent) ? React.cloneElement(ActionComponent, {
				ref: actionRef,
				onHideAction: handleHideAction,
				getCheckedSourceData
			}) : null}
			<OverviewListTable
				className={"debit-list-table"}
				list={isEqual(activeAction, BATCH_DEDUCTION) ? sortedPaymentList : filteredData}
				headerRow={!!activeAction ? handleRowForAction : headerRow}
				tableRowHeaderProps={{
					headerCheckBoxRef,
					onHeaderChange: handleHeaderChange,
					...tableRowHeaderProps
				}}
				tableRowBodyProps={{
					bodyRowClass: isEqual(activeAction, BATCH_DEDUCTION) ? {
						key: "canPayment",
						value: false,
						className: "overview-item-invalid"
					} : undefined,
					dialogRef,
					refresh,
					activeAction,
					isRevealed,
					checkBoxsRef: checkBoxsRef,
					onCheckBoxChange: handleCellChange,
					getCheckedItems: getCheckedItems,
					...tableRowBodyProps
				}}
				isGray
			/>
		</>
	);
});

export default BatchActionTable;
